import { useState } from 'react';

import type { RsvpResponse, RsvpRow, ScheduledSession, SermonOnCard } from '@contracts/index';

import { ApiError, api } from '../api';
import {
  formatLongDate,
  formatTime,
  formatWeekdayDate,
  relativeDay,
  toLocalInputValue,
  voicePartName,
  words,
} from '../format';
import { ChevronDownIcon, ChevronRightIcon, PlusIcon, TrashIcon, WarningIcon } from '../icons';
import { useBackKey } from '../backKey';
import Ask from './Ask';
import Avatar, { AVATAR_ROW } from './Avatar';
import GatheringForm, { type Plan } from './GatheringForm';
import Unfold from './Unfold';

function answerLabel(row: RsvpRow): string {
  if (row.response === 'going') return 'придёт';
  if (row.response === 'not_going') return 'не придёт';
  return 'не ответил';
}

function answerTone(response: RsvpResponse | null): string | undefined {
  if (response === 'going') return 'success';
  if (response === 'not_going') return 'danger';
  return undefined;
}

/** Just the clock when it is today, the whole date when it is not. */
function opensText(iso: string): string {
  return relativeDay(iso) === 'сегодня' ? formatTime(iso) : formatLongDate(iso);
}

/** «18 да · 3 нет · 19 —», with the zeroes left out. */
/** How many are coming, for somebody who is not going to see the names.
 *
 * `countsLine` below is the regent's, and it is a legend under a heading: «1 да
 * · 39 —» earns its shorthand from the label «Кто ответил» above it and the
 * list of names under it. A singer has neither, and the same string standing
 * alone on her screen is a telegram - which is what it looked like when the
 * heading was drawn for her over nothing at all.
 *
 * The word is «придут», because that is the word on the button she just
 * pressed. Nought is the sentence `countsLine` already has for it.
 */
function goingLine(counts: { going: number; not_going: number; no_answer: number }): string {
  if (counts.going === 0 && counts.not_going === 0) return 'Ещё никто не ответил';
  return `Придут ${counts.going} из ${counts.going + counts.not_going + counts.no_answer}`;
}


function countsLine(counts: { going: number; not_going: number; no_answer: number }): string {
  // Nobody has said anything yet, which is the state every gathering starts in
  // and the one this line was read in first. «2 —» is what it used to say
  // there: the dash means «has not answered» and earns that meaning beside a
  // figure that has - «1 да · 1 —» - but standing alone under «Кто ответил» it
  // reads as a string that failed to render. The sentence below was written for
  // exactly this and could never be reached: `no_answer` is the whole choir
  // until somebody answers, so all three were never nought together.
  if (counts.going === 0 && counts.not_going === 0) return 'ещё никто не ответил';
  const parts: string[] = [];
  if (counts.going > 0) parts.push(`${counts.going} да`);
  if (counts.not_going > 0) parts.push(`${counts.not_going} нет`);
  if (counts.no_answer > 0) parts.push(`${counts.no_answer} —`);
  return parts.length === 0 ? 'ещё никто не ответил' : parts.join(' · ');
}

/**
 * **A subset of `AheadGathering` in `legacy/frontend/src/screens/SessionScreen.tsx`
 * (`:597-985`), not a fork of it.** Every range below is at the pin `d5aa0fa`.
 *
 * Three flows have filled it. F-005 built the host (§0.3 of `docs/specs/features/F-005/spec.md`)
 * for the two acts it owns — changing the plan and taking it off the schedule: `busy`,
 * `failure`, `editing`, `removing`, `id`, `kind`, `when`, `answered`/`kindLocked`, `save()`,
 * `act()`, the two buttons, the prefilled form, the ungated `<p className="failure">` and the
 * removal question. F-006 widened it rather than replacing it (§0.3 of
 * `docs/specs/features/F-006/spec.md`): `opensText()` and the component's own doc comment,
 * `useBackKey(onBack)`, `past`, the header, the overdue notice, «Открыть …» with the open
 * through `act()`, and «Открыть можно будет с …». F-011 (`docs/specs/features/F-011/spec.md`)
 * widened it twice more. With «Твой ответ» (T4): `answerTone()`, `goingLine()` with the two doc
 * comments over it, the local row's setter and its doc comment (`:625-632`), `note` (`:635`),
 * `typed` (`:645`), `answer()` (`:702-718`) and the block (`:759-834`). With «Кто ответил» (T7):
 * `answerLabel()` (`:62-66`), `countsLine()` with the two blank lines over it (`:95-111`), the
 * `myMemberId` prop with its doc comment (`:609`, `:616-620`), `expanded` (`:636`), and the
 * block, its fold and the named rows (`:863-914`). F-024's T12
 * (`docs/specs/features/F-024/plan.md` §3) brought the last of it: the programme door
 * (`:848-861`), with the `onOpenProgram` prop (`:612`, `:623`), which only the door reads.
 *
 * **Nothing of legacy `:597-985` is absent now.** What stands here is the whole component in
 * legacy's order, less the places below that are not theirs.
 *
 * `item` is local state seeded once, with the prop named `item: initial` as `:607` has it, and
 * `answer()` is its one writer — `FND-110`: `open.item` is rewritten under a mounted evening, by
 * `add()` after its reload, and legacy goes on drawing the row it was mounted with (AC-106).
 *
 * `act()` (`:689-700`) is **not** `save`'s helper — `save` has its own `try` at `:666-687`,
 * and the two run their callbacks in opposite orders: `save` is `onChanged(); onBack();`,
 * while the removal's `onBack()` is inside the action and `onChanged()` comes after it, from
 * `act` itself. Both cost the list two reloads and the shell two refreshes, which is
 * `FND-071` and is kept.
 *
 * Everything below is the old app's, comments included, with **three places that are not**.
 * Two were reflowed by this tree's prettier, whose width is 100, before F-011's T1 put this file
 * on `.prettierignore`, and stay as they stand: the `setFailure(...)` at their `:683` is 120
 * columns and takes three lines here, and the quiet button's opening tag, five lines at their
 * `:929-933`, is on one here. Every string and attribute in both is character for character
 * theirs. The third is the signature's first line (`:606`): `export default function` where
 * theirs is a local `function`. Since the ignore line, what legacy writes the formatter would
 * change — the two blank lines at their `:95-96`, and `:873-913` two columns short of their block
 * — is kept as written.
 *
 * **What holds those lines to their original is `scripts/ported-partial.txt`.** Its rows for
 * this file name `screens/SessionScreen.tsx` in a fifth field (`FND-083`), because deriving the
 * legacy path from this file's own name would point at nothing, and `scripts/check-css-port.sh`
 * compares every registered run with legacy's, line for line, on every `scripts/check.sh`. The
 * rows are line numbers, so an edit above a run moves it and is re-measured in the same commit.
 * Between the runs are this tree's imports, the two reflows and the signature's first line.
 */

/**
 * What the «Проповедь» block says: who prepares one this week, who preached, or that nobody did —
 * and null for a rehearsal beyond this week, whose list is not known yet. C-002 (`FND-1591`,
 * 2026-10-06) replaced legacy's one name or «никто не поставлен» here, the hole in the run at
 * their `:684` that `scripts/ported-partial.txt` steps around.
 */
function sermonLine(sermon: SermonOnCard): string | null {
  if (sermon.status === 'done') return sermon.display_name;
  if (sermon.status === 'missed') return 'не было';
  if (sermon.preparing.length === 0) return null;
  return `готовят: ${sermon.preparing.map((one) => one.display_name).join(', ')}`;
}
/**
 * A gathering that has not happened yet, and everything about that evening.
 *
 * What used to be a card in the schedule, plus the two doors that hung off it —
 * the programme and the sermon. The answer pair is first because it is what a
 * singer opened this for; «Кто ответил» is folded into a line with the count in
 * it, because expanded it does not fit 812px and the system's own rule is that
 * content ends 16px above the tab bar.
 */
export default function AheadGathering({
  item: initial,
  isRegent,
  myMemberId,
  onBack,
  onChanged,
  onOpenProgram,
}: {
  item: ScheduledSession;
  isRegent: boolean;
  /**
   * Whose face preens rather than recoiling under a finger. `RsvpRow` carries
   * no «this is you» of its own, and one row in this list is the reader's.
   */
  myMemberId: number;
  onBack: () => void;
  onChanged: () => void;
  onOpenProgram: () => void;
}) {
  /**
   * This screen's own copy of the evening.
   *
   * An answer comes back as the whole row, so the counts under the pair move
   * with the tap that changed them rather than after a reload of the list
   * behind. The list is told separately, on the way out.
   */
  const [item, setItem] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [shared, setShared] = useState<boolean | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [editing, setEditing] = useState(false);
  useBackKey(onBack);

  const id = item.session.session_id;
  const kind = words(item.session.kind);
  const when = item.session.scheduled_start ?? item.session.effective_start;
  const past = item.session.scheduled_start !== null && new Date(when).getTime() < Date.now();
  const typed = note ?? item.my_note ?? '';
  /**
   * «Причину увидят все» — C-018 (`FND-1613`). Ticked for a reason not saved yet, as the
   * owner chose; a saved reason comes up the way it was saved.
   */
  const savedPublic = item.my_note === null ? true : item.my_note_public;
  const everybody = shared ?? savedPublic;
  const unsaved =
    typed.trim() !== (item.my_note ?? '') || (typed.trim() !== '' && everybody !== savedPublic);
  /**
   * Whether the kind is still the regent's to change, and why not when it is
   * not. An answer is about an evening a person was told the shape of, and a
   * place in a programme exists only at a service - the server refuses both,
   * and the form says so before the tap rather than after it.
   */
  const answered = item.counts.going + item.counts.not_going > 0;
  const kindLocked =
    answered || item.program_count > 0
      ? 'Вид уже не поменять: на эту встречу ответили или записались в программу.'
      : null;

  /**
   * Moving the evening, renaming it, or changing what it is.
   *
   * The same three fields the schedule's own form has, prefilled - it is that
   * form, not a copy of it. The answers survive because the row keeps its id:
   * removing the gathering and booking it again is what loses them, and until
   * this existed that was the only way to change an hour.
   */
  async function save(plan: Plan): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      await api.editScheduledSession(id, {
        title: plan.title.trim() === '' ? null : plan.title.trim(),
        // The entered time is local; new Date parses it as local and sends it
        // with an offset, so the server receives the exact moment.
        scheduledStart: new Date(plan.start).toISOString(),
        kind: plan.kind,
      });
      setEditing(false);
      // The card this screen was opened from is now wrong in three places, and
      // it is the list's copy: leaving is cheaper and truer than patching it.
      onChanged();
      onBack();
    } catch (error) {
      setFailure(
        error instanceof ApiError ? error.message : `Не удалось изменить ${kind.accusative}`,
      );
    } finally {
      setBusy(false);
    }
  }

  async function act(action: () => Promise<unknown>, fallback: string): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      await action();
      onChanged();
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : fallback);
    } finally {
      setBusy(false);
    }
  }

  /**
   * The note travels with every answer because the server stores what it is
   * given: sending the response alone would erase a reason already typed, and
   * the singer would watch her own words vanish for pressing «Не приду» twice.
   */
  async function answer(
    response: RsvpResponse,
    text: string | null = null,
    everybodySees = false,
  ): Promise<void> {
    setBusy(true);
    setFailure(null);
    try {
      setItem(await api.answerSession(id, response, text, text !== null && everybodySees));
      onChanged();
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'Ответ не сохранился');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="screen-head">
        <p className={past ? 'date-line date-line--overdue' : 'date-line'}>
          {formatWeekdayDate(when)} · <span className="num">{formatTime(when)}</span>
          {!past && relativeDay(when) !== null && ` · ${relativeDay(when)}`}
        </p>
        <h1 className="screen-title">{item.session.title ?? kind.titled}</h1>
      </div>

      {isRegent && past && (
        <p className="notice">
          <WarningIcon size={18} />
          Время прошло, {kind.accusative} так и не открыли.
        </p>
      )}

      {/* The accent, and the only place on this screen that carries it: opening
          the gathering that is happening is what the regent came for. */}
      {isRegent && item.can_open && (
        <button
          className={past ? 'btn btn--outline' : 'btn btn--primary'}
          disabled={busy}
          onClick={() =>
            void act(async () => {
              await api.openScheduledSession(id);
              onBack();
            }, `Не удалось открыть ${kind.accusative}`)
          }
        >
          {!past && <PlusIcon size={20} />}
          {past ? 'Открыть сейчас' : `Открыть ${kind.accusative}`}
        </button>
      )}

      {isRegent && !item.can_open && item.opens_at !== null && (
        <p className="hint">Открыть можно будет с {opensText(item.opens_at)}.</p>
      )}

      {/* The answer, and it is first: it is the errand. A regent's too since
          C-012 (`FND-1606`) - he sat in «—» on every gathering and could not say
          he would be away. The figure and the hint under it stay the singer's:
          the regent reads the counts in «Кто ответил», and is marked by opening.
          Not on an evening whose hour has gone - C-015 (`FND-1610`): only the
          regent ever sees one, and an answer to it says nothing. */}
      {!past && (
        <div className="block">
          <span className="label">Твой ответ</span>
          <div className="rsvp">
            <button
              className={
                item.my_response === 'going'
                  ? 'answer answer--input'
                  : 'answer answer--input answer--blank'
              }
              data-tone={answerTone(item.my_response === 'going' ? 'going' : null)}
              data-press="wash"
              disabled={busy}
              onClick={() => void answer('going')}
            >
              {item.my_response === 'going' ? '✓ Приду' : 'Приду'}
            </button>
            <button
              className={
                item.my_response === 'not_going'
                  ? 'answer answer--input'
                  : 'answer answer--input answer--blank'
              }
              data-tone={answerTone(item.my_response === 'not_going' ? 'not_going' : null)}
              data-press="wash"
              disabled={busy}
              onClick={() =>
                void answer('not_going', typed.trim() === '' ? null : typed.trim(), everybody)
              }
            >
              {item.my_response === 'not_going' ? '✗ Не приду' : 'Не приду'}
            </button>
          </div>

          {/* The field appears only after the answer is in, and saves
              separately: asking for a reason before accepting the answer would
              make declining cost more than turning up. */}
          {/* C-016 (FND-1611): the field, its note and «Сохранить причину» open and close
              through `Unfold` instead of arriving in one frame and shoving everything under
              them down by about 80px under the finger that pressed. The wrapper carries
              «Твой ответ»'s 6px between the three, `.reason-fold` in the stylesheet. */}
          <Unfold open={item.my_response === 'not_going'}>
            <div className="reason-fold">
              <input
                className="reason"
                placeholder="Причина, если хочешь"
                maxLength={100}
                value={typed}
                onChange={(event) => setNote(event.target.value)}
              />
              {/* C-018 (`FND-1613`): the box the owner asked for, ticked by default. Unticked,
                  a quiet line under its words says who reads the reason now — variant «А»,
                  chosen 2026-10-09 over the block that read as stuck together. */}
              <div className="reason-who">
                <label className="reason-share">
                  <input
                    type="checkbox"
                    checked={everybody}
                    onChange={(event) => setShared(event.target.checked)}
                  />
                  Причину увидят все
                </label>
                {!everybody && <p className="reason-note">Сейчас её увидит только регент</p>}
              </div>
              <Unfold open={unsaved}>
                <button
                  className="btn btn--outline"
                  disabled={busy}
                  onClick={() => {
                    const kept = typed.trim();
                    void answer('not_going', kept === '' ? null : kept, everybody).then(() => {
                      setNote(null);
                      setShared(null);
                    });
                  }}
                >
                  Сохранить причину
                </button>
              </Unfold>
            </div>
          </Unfold>

          {/* How many are coming, where the answer was given rather than in a
              section of its own. It used to stand under «Кто ответил» - a
              heading whose contents are the names, and the names are the
              regent's - so a singer got the heading, the shorthand, and half a
              screen of nothing under it, which reads as a list that failed to
              load rather than as one she is not shown. */}
          {!isRegent && <p className="rsvp-counts">{goingLine(item.counts)}</p>}

          {!isRegent && (
            <p className="hint">
              {item.my_response === null
                ? `Это ответ, а не отметка: отметиться нужно будет на ${kind.prepositional}.`
                : `Ответ можно поменять, пока ${kind.nominative} ${kind.startedForm}.`}
            </p>
          )}
        </div>
      )}

      {/* Whose sermon this evening is. Nothing at all where there is nothing to
          say — a service, which has a programme instead, and a rehearsal past
          this week, whose list is not known yet. C-002 (`FND-1591`). */}
      {item.sermon !== null && sermonLine(item.sermon) !== null && (
        <div className="block">
          <span className="label">Проповедь</span>
          <p className="rsvp-counts">{sermonLine(item.sermon)}</p>
        </div>
      )}

      {/* The programme of a service, as a door. It was one on the card in the
          list; here it is one block of the evening it belongs to. */}
      {item.session.kind === 'service' && (
        <div className="block">
          <span className="label">
            Программа{' '}
            {item.program_count > 0 && <span className="num">· {item.program_count}</span>}
          </span>
          <button className="btn btn--quiet" onClick={onOpenProgram}>
            {item.program_count > 0 ? 'Открыть программу' : 'Записаться'}
            <ChevronRightIcon size={16} quiet />
          </button>
        </div>
      )}

      {/* Who answered, folded into a line with the count in it. Expanded, forty
          rows do not fit 812px above a tab bar — which is the budget frame 12c
          of the audit counts rather than claims.

          Everybody's since C-015 (`FND-1610`): the owner wanted the singers to
          see who is coming too. The reasons stay the regent's - the server
          sends a singer none - and no chip is a button: nobody answers for
          anybody else, which took back C-012's tap on another regent's row. */}
      {(
        <div className="block">
          <div className="split">
            <span className="label">Кто ответил</span>
          <span className="rsvp-counts">{countsLine(item.counts)}</span>
        </div>
        <button
          className="btn btn--quiet"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Свернуть' : 'Показать по именам'}
          <ChevronDownIcon size={16} quiet turned={expanded} />
        </button>
        <Unfold open={expanded}>
          <ul className="live">
            {item.rows.map((row) => (
              <li
                key={row.member_id}
                className={row.response === null ? 'live-row live-row--waiting' : 'live-row'}
              >
                <Avatar
                  eyes={row.avatar_eyes}
                  color={row.avatar_color}
                  rate={row.attendance_rate_30d}
                  size={AVATAR_ROW}
                  status={row.response ?? 'plain'}
                  mine={row.member_id === myMemberId}
                />
                <span className="live-body">
                  <span className="live-name">{row.display_name}</span>
                  <span className="live-sub">{voicePartName(row.voice_part)}</span>
                  {/* C-022: the reason under the voice part, wrapping inside the row. In the
                      chip it was `nowrap` and `flex: none`, so a long one pushed the name out
                      and the row past the screen's edge. */}
                  {row.response === 'not_going' && row.note !== null && (
                    <span className="live-sub live-sub--wrap rsvp-reason">{row.note}</span>
                  )}
                </span>
                <span
                  className={row.response === null ? 'answer answer--blank' : 'answer'}
                  data-tone={answerTone(row.response)}
                >
                  {answerLabel(row)}
                </span>
              </li>
            ))}
          </ul>
        </Unfold>
      </div>
      )}

      {/* Changing the plan, and then removing it. In that order because they
          are the same errand at two prices: a rehearsal that moved an hour is
          the ordinary case, and losing forty answers over it was the whole
          defect. Removing stays last and stays quiet - what it takes with it is
          the choir's answers, and nothing in the app puts a removed gathering
          back. */}
      {isRegent && (
        <button className="btn btn--outline" disabled={busy} onClick={() => setEditing(true)}>
          Перенести или переименовать
        </button>
      )}

      {isRegent && (
        <button
          className="btn btn--quiet btn--danger-quiet"
          disabled={busy}
          onClick={() => setRemoving(true)}
        >
          <TrashIcon size={18} />
          Убрать {kind.accusative} из расписания
        </button>
      )}

      {/* Prefilled from this evening, and the suggestions are left empty on
          purpose: they answer «when does this choir usually meet», which is the
          question when a gathering is being made and not when one is being
          moved to a named hour. With nothing to offer `WhenField` opens its
          wheel with the form, which is what moving a date wants anyway. */}
      <GatheringForm
        open={editing}
        mode="edit"
        initial={{
          kind: item.session.kind,
          start: toLocalInputValue(new Date(when)),
          title: item.session.title ?? '',
        }}
        held={[]}
        busy={busy}
        kindLocked={kindLocked}
        failure={editing ? failure : null}
        onSave={(plan) => void save(plan)}
        onClose={() => setEditing(false)}
      />

      {failure !== null && <p className="failure">{failure}</p>}

      <Ask
        open={removing}
        title={`Убрать ${kind.accusative} ${formatLongDate(when)}?`}
        cost={
          <>
            {kind.titled} пропадёт из расписания вместе с ответами, которые уже дали. Вернуть{' '}
            {kind.accusative} нечем, только завести заново.
          </>
        }
        verb="Убрать"
        busy={busy}
        failure={failure}
        onConfirm={() =>
          void act(async () => {
            await api.removeScheduledSession(id);
            setRemoving(false);
            onBack();
          }, `Не удалось убрать ${kind.accusative}`)
        }
        onClose={() => setRemoving(false)}
      />
    </>
  );
}
